import { convertData } from "./calc";
import { EXPORT_APP_ID, EXPORT_VERSION, OBJECT_URL_LIFETIME_MS } from "./constants";
import { plural, transfer as copy } from "./content";
import { cleanCurrency, cleanUnit, listOf, num, sanitizeProfile, sanitizeVehicle } from "./sanitize";
import type { Data, Profile, Vehicle } from "./types";

// Export/import is entirely in the browser. Each vehicle and profile carries its
// own id in the export, so re-importing it later updates it in place rather than
// creating a duplicate (see mergeById). The payload format is unchanged from the
// original "cost-per-mile" prototype, so its exports can be imported here.

export type ImportErrorCode = "empty-input" | "not-ours" | "newer" | "empty";

export class ImportError extends Error {
  constructor(public code: ImportErrorCode) {
    super(code);
    this.name = "ImportError";
  }
}

export function importErrorText(err: unknown): string {
  switch (err instanceof ImportError ? err.code : "not-ours") {
    case "empty-input":
      return copy.errors.emptyInput;
    case "newer":
      return copy.errors.newer;
    default:
      return copy.errors.notOurs;
  }
}

export function toPayload(d: Data) {
  return {
    app: EXPORT_APP_ID,
    v: EXPORT_VERSION,
    currency: d.currency,
    distanceUnit: d.distanceUnit,
    profiles: d.profiles.map((p) => ({
      id: p.id,
      name: p.name,
      distanceAmount: p.distanceAmount,
      distancePeriod: p.distancePeriod,
      homeShare: p.homeShare,
    })),
    vehicles: d.vehicles.map((v) => ({
      id: v.id,
      name: v.name,
      fuel: v.fuel,
      mpg: v.mpg,
      iceHomePrice: v.iceHomePrice,
      iceAwayPrice: v.iceAwayPrice,
      evMiPerKwh: v.evMiPerKwh,
      evHomeRate: v.evHomeRate,
      evAwayRate: v.evAwayRate,
      costItems: v.costItems.map((i) => ({ name: i.name, amount: i.amount, period: i.period })),
    })),
  };
}

/** Incoming data is never read through; see sanitize.ts for how it is rebuilt. */
export function fromPayload(d: unknown): Data {
  const o = d as Record<string, unknown> | null;
  if (!o || typeof o !== "object" || o.app !== EXPORT_APP_ID) throw new ImportError("not-ours");
  if (num(o.v) > EXPORT_VERSION) throw new ImportError("newer");

  const profiles: Profile[] = listOf(o.profiles).map(sanitizeProfile);
  const vehicles: Vehicle[] = listOf(o.vehicles).map((v) => sanitizeVehicle(v));
  if (!profiles.length || !vehicles.length) throw new ImportError("empty");

  return { currency: cleanCurrency(o.currency), distanceUnit: cleanUnit(o.distanceUnit), profiles, vehicles };
}

export function parseIncoming(text: string): Data {
  const t = text.trim();
  if (!t) throw new ImportError("empty-input");
  if (!t.startsWith("{")) throw new ImportError("not-ours");
  let json: unknown;
  try {
    json = JSON.parse(t);
  } catch {
    throw new ImportError("not-ours");
  }
  return fromPayload(json);
}

export interface Merge<T> {
  result: T[];
  added: number;
  updated: number;
}

/** Match incoming items to existing ones by id: known ones are updated in place, new ones appended. */
export function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): Merge<T> {
  const indexById = new Map(existing.map((item, i) => [item.id, i]));
  const result = existing.slice();
  let added = 0;
  let updated = 0;
  for (const item of incoming) {
    const at = indexById.get(item.id);
    if (at !== undefined) {
      result[at] = item;
      updated++;
    } else {
      indexById.set(item.id, result.length);
      result.push(item);
      added++;
    }
  }
  return { result, added, updated };
}

export function applyImport(current: Data, incoming: Data, mode: "replace" | "add"): { data: Data; note: string } {
  if (mode === "replace") {
    return { data: incoming, note: copy.loaded(describe(incoming)) };
  }
  const converted = convertData(incoming, current.distanceUnit);
  const v = mergeById(current.vehicles, converted.vehicles);
  const p = mergeById(current.profiles, converted.profiles);
  const added = v.added + p.added;
  const updated = v.updated + p.updated;
  const bits: string[] = [];
  if (added) bits.push(copy.added(added));
  if (updated) bits.push(copy.updated(updated));
  return {
    data: { ...current, vehicles: v.result, profiles: p.result },
    note: bits.length ? bits.join(", ") + "." : copy.nothingNew,
  };
}

export const describe = (d: Data) =>
  `${plural(d.vehicles.length, "vehicle")} and ${plural(d.profiles.length, "driving profile")}`;

export function downloadJson(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
}
